import React, { useCallback, useImperativeHandle, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { FlexBox, FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import { Spacing } from '@ecp/common/src/layouts/Spacing';
import ImageCard from '@ecp/common/src/components/file/bo/ImageCard';
import UploaderDropzone from '@ecp/common/src/components/file/bo/UploaderDropzone';
import { FILE_EXTENSION, FILE_SIZE } from '@ecp/common/src/components/unstyled/input/FileUploaderUnstyled';
import ImageViewPopup from '@ecp/common/src/components/modal/bo/popup/ImageViewPopup';
import {
  GuideMessage,
  GuideMessageContainer,
  GuideMessagesWrapper,
  ImageFileUploaderContainer,
} from '@ecp/common/src/components/file/ImageFileUploader';
import useImageFileUploader from '@ecp/common/src/components/file/useImageFileUploader';
import { IMAGE_MESSAGE, IMAGE_STATUS } from '@ecp/common/src/const/imageFile';
import { ReactComponent as InfoIcon } from '@ecp/common/src/assets/icon/icon__tooltip-info.svg';

import { useAlertDialog } from '@bo-hooks/common/useModal';

const ImageFileUploader = React.forwardRef(
  (
    {
      guideMessages,
      imageFileList,
      setImageFileList,
      imageItemList,
      draggable,
      width,
      multiUpload,
      multiUploadMaxCount,
      cardProps,
      readOnly,
      extension,
      alignItems,
      maxSize,
      text,
    },
    ref
  ) => {
    const [imageViewPopupOpen, setImageViewPopupOpen] = useState(false);
    const [imageViewIndex, setImageViewIndex] = useState(0);
    const [imageViewList, setImageViewList] = useState([]);
    const [error, setError] = useState(false);
    const { showAlertDialog } = useAlertDialog();

    const { drop, filteredImageFileList, handleDelete, handleMove, handleMultiUpload, showDropzone } =
      useImageFileUploader(
        ref,
        imageFileList,
        setImageFileList,
        multiUploadMaxCount,
        maxSize,
        showAlertDialog,
        readOnly,
        multiUpload
      );

    useImperativeHandle(ref, () => ({
      hasError: () => error,
    }));

    const handleError = useCallback(
      ({ errors }) => {
        if (errors.length > 0) {
          setError(true);
          showAlertDialog(errors[0]);
        }
      },
      [showAlertDialog]
    );

    const handleChange = useCallback(
      (reqType, imageFile) => {
        const updatedImageFileList = [...imageFileList];
        const prevNewIdxToRemove = updatedImageFileList.findIndex(
          ({ type, status }) => type === reqType && (status === IMAGE_STATUS.NEW || status === IMAGE_STATUS.COPY)
        );
        if (prevNewIdxToRemove >= 0) updatedImageFileList.splice(prevNewIdxToRemove, 1);

        const registeredIdxToDelete = updatedImageFileList.findIndex(
          ({ type, status }) => type === reqType && status === IMAGE_STATUS.REGISTERED
        );
        if (registeredIdxToDelete >= 0) {
          updatedImageFileList.splice(registeredIdxToDelete, 1, {
            ...updatedImageFileList[registeredIdxToDelete],
            status: IMAGE_STATUS.NEED_TO_DEL,
          });
        }

        if (!imageFile.url && !imageFile.file) {
          setImageFileList([...updatedImageFileList]);
        } else {
          setImageFileList([...updatedImageFileList, { ...imageFile, type: reqType, status: IMAGE_STATUS.NEW }]);
        }
      },
      [imageFileList, setImageFileList]
    );

    const handleZoomIn = useCallback(
      (index, imageFile) => {
        if (multiUpload && readOnly) {
          setImageViewIndex(index);
          setImageViewList(filteredImageFileList);
        } else {
          setImageViewList([imageFile]);
        }
        setImageViewPopupOpen(true);
      },
      [multiUpload, readOnly, filteredImageFileList]
    );

    const getFile = useCallback(
      (reqType) => {
        const file = filteredImageFileList.find(({ type }) => type === reqType);
        return file || { file: '', url: '' };
      },
      [filteredImageFileList]
    );

    return (
      <ImageFileUploaderContainer alignItems={alignItems}>
        <ImageCardsWrapper ref={drop} style={{ width: width }}>
          {!multiUpload
            ? imageItemList.map((item, index) => (
                <ImageCard
                  index={index}
                  key={item.key}
                  keyValue={item.key}
                  desc={item.desc}
                  value={getFile(item.key)}
                  onChange={(imageFile) => handleChange(item.key, imageFile)}
                  onError={handleError}
                  draggable={draggable}
                  onMove={handleMove}
                  multiUpload={multiUpload}
                  readOnly={readOnly}
                  extension={extension}
                  onZoom={handleZoomIn}
                  maxSize={maxSize}
                  text={text}
                  {...cardProps}
                />
              ))
            : filteredImageFileList.map((item, index) => (
                <ImageCard
                  index={index}
                  key={index}
                  keyValue={item.type}
                  value={item}
                  onDelete={(keyValue) => handleDelete(keyValue)}
                  draggable={draggable}
                  onMove={handleMove}
                  multiUpload={multiUpload}
                  readOnly={readOnly}
                  extension={extension}
                  onZoom={handleZoomIn}
                  {...cardProps}
                />
              ))}
        </ImageCardsWrapper>
        <ImageViewPopup
          open={imageViewPopupOpen}
          onClose={() => setImageViewPopupOpen(false)}
          items={imageViewList}
          initialSlide={imageViewIndex}
        />
        {showDropzone && (
          <>
            <Spacing top={'14px'} />
            <FileUploaderWrapper>
              <UploaderDropzone
                guideMessage={IMAGE_MESSAGE.DRAG_DROP}
                onChange={handleMultiUpload}
                multiple
                maxCount={multiUploadMaxCount}
                extension={extension}
                maxSize={FILE_SIZE.IMAGE}
              />
            </FileUploaderWrapper>
          </>
        )}
        {guideMessages && (
          <>
            <Spacing top={'6px'} />
            <GuideMessagesWrapper>
              {guideMessages.map((message, index) => (
                <GuideMessageContainer key={index}>
                  <GuideIcon />
                  <GuideMessage>{message}</GuideMessage>
                </GuideMessageContainer>
              ))}
            </GuideMessagesWrapper>
          </>
        )}
      </ImageFileUploaderContainer>
    );
  }
);

ImageFileUploader.displayName = 'ImageFileUploader';

ImageFileUploader.propTypes = {
  value: PropTypes.any,
  guideMessages: PropTypes.arrayOf(PropTypes.string),
  registeredImages: PropTypes.arrayOf(PropTypes.object),
  imageFileList: PropTypes.arrayOf(PropTypes.object),
  setImageFileList: PropTypes.func,
  imageItemList: PropTypes.arrayOf(PropTypes.object),
  draggable: PropTypes.bool,
  width: PropTypes.string,
  multiUpload: PropTypes.bool,
  multiUploadMaxCount: PropTypes.number,
  showAlertDialog: PropTypes.func,
  readOnly: PropTypes.bool,
  cardProps: PropTypes.shape({
    width: PropTypes.string,
    height: PropTypes.string,
  }),
  extension: PropTypes.arrayOf(PropTypes.string),
  alignItems: PropTypes.string,
  maxSize: PropTypes.number,
  text: PropTypes.string,
};

ImageFileUploader.defaultProps = {
  imageFileList: [],
  imageItemList: [
    { desc: '공통\n(640*640)', key: 'COMMON', width: 640, height: 640 },
    { desc: 'PC\n(500*290)', key: 'PC', width: 500, height: 290 },
    { desc: 'MO\n(640*368)', key: 'MO', width: 640, height: 368 },
  ],
  guideMessage: '',
  draggable: false,
  width: 'fit-content',
  multiUpload: false,
  multiUploadMaxCount: 10,
  cardProps: {},
  extension: FILE_EXTENSION.IMAGE,
  maxSize: FILE_SIZE.IMAGE,
};

export default ImageFileUploader;

const ImageCardsWrapper = styled(FlexBox)`
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 14px;
`;

const GuideIcon = styled(InfoIcon)`
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  flex-grow: 0;
`;

const FileUploaderWrapper = styled(FlexBoxColumn)`
  position: relative;
  width: 100%;
  gap: 6px;
`;
